import React, { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Loader2, Plus, X, Copy, Check, Users, Handshake, AlertTriangle,
  Power, Eye, IndianRupee, RotateCcw, Link2,
} from 'lucide-react';

const STATUS_CLS = {
  ACTIVE: 'bg-emerald-100 text-emerald-700',
  INACTIVE: 'bg-neutral-200 text-neutral-600',
  PENDING: 'bg-amber-100 text-amber-700',
  REJECTED: 'bg-rose-100 text-rose-600',
};
const COMM_CLS = {
  ACCRUED: 'bg-emerald-100 text-emerald-700',
  NEEDS_ADMIN_REVIEW: 'bg-amber-100 text-amber-700',
  REVERSED: 'bg-rose-100 text-rose-600',
  PAID: 'bg-sky-100 text-sky-700',
};

const inr = (n) => (n === null || n === undefined ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);

function Modal({ open, onClose, children, testid, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} border border-fab-primary-lighter max-h-[90vh] overflow-y-auto`}>
        {children}
      </div>
    </div>
  );
}

export default function AdminAffiliate({ type = 'PROFESSIONAL' }) {
  const isPro = type === 'PROFESSIONAL';
  const title = isPro ? 'Professional Marketers' : 'Community Marketers';
  const ratePct = isPro ? 10 : 5;

  const [marketers, setMarketers] = useState(null);
  const [stats, setStats] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteNote, setInviteNote] = useState('');
  const [copied, setCopied] = useState('');
  const [communityReg, setCommunityReg] = useState(null);

  const load = useCallback(() => {
    api.get(`/affiliate/admin/marketers?type=${type}`).then(({ data }) => setMarketers(data.marketers || [])).catch(() => setMarketers([]));
    api.get('/affiliate/admin/stats').then(({ data }) => setStats(data)).catch(() => {});
    if (isPro) api.get('/affiliate/admin/invitations').then(({ data }) => setInvitations(data.invitations || [])).catch(() => {});
    else api.get('/affiliate/admin/community-registration').then(({ data }) => setCommunityReg(data)).catch(() => {});
  }, [type, isPro]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (mid) => {
    setDetail(null); setDetailOpen(true);
    api.get(`/affiliate/admin/marketers/${mid}`).then(({ data }) => setDetail(data)).catch(() => { toast.error('Failed to load'); setDetailOpen(false); });
  };

  const refreshDetail = () => { if (detail?.marketer?.id) openDetail(detail.marketer.id); load(); };

  const toggleStatus = async (m) => {
    const next = m.status === 'ACTIVE' ? 'deactivate' : 'activate';
    try {
      await api.post(`/affiliate/admin/marketers/${m.id}/${next}`);
      toast.success(`Marketer ${next}d`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const createInvite = async () => {
    try {
      const { data } = await api.post('/affiliate/admin/invitations', { email: inviteEmail || null, note: inviteNote || null });
      const link = `${window.location.origin}/affiliate/join/${data.token}`;
      navigator.clipboard?.writeText(link).catch(() => {});
      toast.success('Invitation created — link copied');
      setInviteOpen(false); setInviteEmail(''); setInviteNote('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const revokeInvite = async (id) => {
    try { await api.delete(`/affiliate/admin/invitations/${id}`); toast.success('Revoked'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const copyInviteLink = (token, id) => {
    const link = `${window.location.origin}/affiliate/join/${token}`;
    navigator.clipboard?.writeText(link);
    setCopied(id); setTimeout(() => setCopied(''), 1500);
  };

  return (
    <div className="space-y-6" data-testid={`admin-affiliate-${type.toLowerCase()}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-fab-text flex items-center gap-2">
            <Handshake className="w-6 h-6 text-fab-primary" /> {title}
          </h1>
          <p className="text-[13px] text-fab-text-secondary mt-0.5">Fixed commission rate: <b>{ratePct}%</b> of each successful subscription payment (ex-GST).</p>
        </div>
        {isPro && (
          <button onClick={() => setInviteOpen(true)} data-testid="affiliate-invite-btn" className="btn-primary px-4 py-2 text-[13px] flex items-center gap-2">
            <Plus className="w-4 h-4" /> Invite Marketer
          </button>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: isPro ? 'Professional' : 'Community', value: isPro ? stats.professional : stats.community, icon: Users },
            { label: 'Active', value: stats.active_marketers, icon: Power },
            { label: 'Referred Sellers', value: stats.referred_sellers, icon: Link2 },
            { label: 'Payable Accrued', value: inr(stats.total_accrued_payable), icon: IndianRupee },
          ].map((s) => (
            <div key={s.label} className="fab-card p-4" data-testid={`affiliate-stat-${s.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
              <div className="text-[11px] uppercase tracking-wide text-fab-text-muted font-semibold">{s.label}</div>
              <div className="text-xl font-bold text-fab-text mt-1">{s.value ?? '—'}</div>
            </div>
          ))}
        </div>
      )}

      {stats?.needs_review > 0 && (
        <div className="flex items-center gap-2 text-[13px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="affiliate-review-banner">
          <AlertTriangle className="w-4 h-4" /> {stats.needs_review} commission(s) need admin review (missing/invalid GST invoice). Verify the base amount in a marketer's detail view.
        </div>
      )}

      {!isPro && communityReg && (
        <div className="fab-card p-5 flex flex-col sm:flex-row items-center gap-5" data-testid="community-reg-card">
          <img src={communityReg.qr_data_url} alt="Community registration QR" className="w-28 h-28 rounded-lg border border-fab-primary-lighter" data-testid="community-reg-qr" />
          <div className="flex-1 w-full">
            <div className="text-[12px] uppercase tracking-wide text-fab-text-muted font-semibold mb-1">Public Community registration link</div>
            <div className="flex items-center gap-2">
              <input readOnly value={communityReg.url} data-testid="community-reg-link" className="fab-input flex-1 px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] font-mono" />
              <button onClick={() => { navigator.clipboard?.writeText(communityReg.url); setCopied('community'); setTimeout(() => setCopied(''), 1500); }} data-testid="community-reg-copy" className="btn-primary px-3 py-2 flex items-center gap-1 text-[13px]">
                {copied === 'community' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} Copy
              </button>
            </div>
            <p className="text-[12px] text-fab-text-muted mt-2">Anyone with this link can register as a Community marketer and is auto-activated at 5%. Share the link or QR openly.</p>
          </div>
        </div>
      )}

      {isPro && invitations.filter((i) => i.status === 'PENDING').length > 0 && (
        <div className="fab-card p-4">
          <div className="text-[13px] font-semibold text-fab-text mb-2">Pending Invitations</div>
          <div className="space-y-2">
            {invitations.filter((i) => i.status === 'PENDING').map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-3 text-[13px] border border-fab-primary-lighter rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <div className="font-medium text-fab-text truncate">{i.email || 'Open invite'}</div>
                  {i.note && <div className="text-fab-text-muted text-[12px] truncate">{i.note}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => copyInviteLink(i.token, i.id)} data-testid={`invite-copy-${i.id}`} className="px-2 py-1 rounded-md border border-fab-primary-lighter hover:bg-fab-elevated flex items-center gap-1">
                    {copied === i.id ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />} Link
                  </button>
                  <button onClick={() => revokeInvite(i.id)} data-testid={`invite-revoke-${i.id}`} className="px-2 py-1 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50">Revoke</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="fab-card overflow-hidden">
        {marketers === null ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-fab-primary" /></div>
        ) : marketers.length === 0 ? (
          <div className="p-10 text-center text-[13px] text-fab-text-secondary">No {title.toLowerCase()} yet.</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-fab-elevated text-fab-text-muted text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2.5">Marketer</th>
                <th className="text-left px-4 py-2.5">Code</th>
                <th className="text-right px-4 py-2.5">Sellers</th>
                <th className="text-right px-4 py-2.5">Balance Due</th>
                <th className="text-center px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {marketers.map((m) => (
                <tr key={m.id} className="border-t border-fab-primary-lighter hover:bg-fab-elevated/50" data-testid={`marketer-row-${m.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-fab-text">{m.name}</div>
                    <div className="text-fab-text-muted text-[12px]">{m.email}</div>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-fab-primary">{m.referral_code}</td>
                  <td className="px-4 py-2.5 text-right">{m.referred_sellers}</td>
                  <td className="px-4 py-2.5 text-right font-semibold">{inr(m.summary?.balance_due)}</td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_CLS[m.status] || 'bg-neutral-100'}`}>{m.status}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openDetail(m.id)} data-testid={`marketer-view-${m.id}`} className="px-2 py-1 rounded-md border border-fab-primary-lighter hover:bg-fab-elevated flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> View</button>
                      <button onClick={() => toggleStatus(m)} data-testid={`marketer-toggle-${m.id}`} className={`px-2 py-1 rounded-md border flex items-center gap-1 ${m.status === 'ACTIVE' ? 'border-rose-200 text-rose-600 hover:bg-rose-50' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'}`}>
                        <Power className="w-3.5 h-3.5" /> {m.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Invite modal */}
      <Modal open={inviteOpen} onClose={() => setInviteOpen(false)} testid="affiliate-invite-modal">
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold">Invite a Professional Marketer</h3>
            <button onClick={() => setInviteOpen(false)}><X className="w-5 h-5 text-fab-text-muted" /></button>
          </div>
          <div className="space-y-3">
            <input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="Email (optional)" type="email" data-testid="invite-email-input" className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]" />
            <input value={inviteNote} onChange={(e) => setInviteNote(e.target.value)} placeholder="Internal note (optional)" data-testid="invite-note-input" className="fab-input w-full px-3.5 py-2.5 rounded-lg border border-fab-primary-lighter text-[14px]" />
            <p className="text-[12px] text-fab-text-muted">A private, single-use join link is generated and copied to your clipboard. Only invited people can register as Professional marketers.</p>
            <button onClick={createInvite} data-testid="invite-create-btn" className="btn-primary w-full py-2.5 text-[14px]">Create Invitation</button>
          </div>
        </div>
      </Modal>

      {/* Detail modal */}
      <Modal open={detailOpen} onClose={() => setDetailOpen(false)} testid="affiliate-detail-modal" wide>
        {!detail ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-fab-primary" /></div>
        ) : (
          <MarketerDetail detail={detail} onClose={() => setDetailOpen(false)} onChange={refreshDetail} />
        )}
      </Modal>
    </div>
  );
}

function MarketerDetail({ detail, onClose, onChange }) {
  const { marketer: m, summary, referred_sellers: sellers, commissions, payouts } = detail;
  const [payoutAmt, setPayoutAmt] = useState('');
  const [payoutLabel, setPayoutLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [reviewFor, setReviewFor] = useState(null);
  const [reviewBase, setReviewBase] = useState('');

  const recordPayout = async () => {
    const amt = parseFloat(payoutAmt);
    if (!amt || amt <= 0) return toast.error('Enter a valid amount');
    setBusy(true);
    try {
      await api.post('/affiliate/admin/payouts', { marketer_id: m.id, amount: amt, period_label: payoutLabel || null });
      toast.success('Payout recorded');
      setPayoutAmt(''); setPayoutLabel(''); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };

  const refundAdjust = async (c, mode) => {
    try {
      await api.post('/affiliate/admin/refund-adjust', { commission_id: c.id, mode });
      toast.success('Adjustment recorded');
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const resolveReview = async () => {
    const base = parseFloat(reviewBase);
    if (!base || base <= 0) return toast.error('Enter the verified ex-GST base amount');
    try {
      await api.post(`/affiliate/admin/commissions/${reviewFor.id}/resolve-review`, { base_amount: base });
      toast.success('Commission resolved');
      setReviewFor(null); setReviewBase(''); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="p-5" data-testid="marketer-detail">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-lg font-bold">{m.name}</h3>
          <div className="text-[12px] text-fab-text-muted">{m.email} · {m.phone} · Code <span className="font-mono text-fab-primary">{m.referral_code}</span></div>
        </div>
        <button onClick={onClose}><X className="w-5 h-5 text-fab-text-muted" /></button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        {[['Accrued', inr(summary.total_accrued)], ['Deductions', inr(summary.total_deductions)], ['Paid Out', inr(summary.total_paid_out)], ['Balance Due', inr(summary.balance_due)]].map(([l, v]) => (
          <div key={l} className="rounded-lg bg-fab-elevated px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-fab-text-muted font-semibold">{l}</div>
            <div className="text-[15px] font-bold text-fab-text">{v}</div>
          </div>
        ))}
      </div>

      {/* Bank */}
      <div className="text-[12px] text-fab-text-secondary mb-4 rounded-lg border border-fab-primary-lighter px-3 py-2">
        <b>Payout details:</b> {m.bank_name || '—'} · A/C {m.bank_account_masked || '—'} · IFSC {m.bank_ifsc || '—'} · UPI {m.upi_id || '—'}
      </div>

      {/* Record payout */}
      <div className="mb-4">
        <div className="text-[13px] font-semibold text-fab-text mb-2">Record Manual Payout</div>
        <div className="flex flex-wrap items-center gap-2">
          <input value={payoutAmt} onChange={(e) => setPayoutAmt(e.target.value)} placeholder="Amount ₹" type="number" data-testid="payout-amount" className="fab-input px-3 py-2 rounded-lg border border-fab-primary-lighter text-[14px] w-32" />
          <input value={payoutLabel} onChange={(e) => setPayoutLabel(e.target.value)} placeholder="Period (e.g. Jun 2026)" data-testid="payout-label" className="fab-input px-3 py-2 rounded-lg border border-fab-primary-lighter text-[14px] flex-1 min-w-[140px]" />
          <button onClick={recordPayout} disabled={busy} data-testid="payout-record-btn" className="btn-primary px-4 py-2 text-[13px]">Record</button>
        </div>
      </div>

      {/* Referred sellers */}
      <div className="mb-4">
        <div className="text-[13px] font-semibold text-fab-text mb-2">Referred Sellers ({sellers.length})</div>
        <div className="max-h-32 overflow-y-auto space-y-1">
          {sellers.length === 0 ? <div className="text-[12px] text-fab-text-muted">None yet.</div> : sellers.map((s) => (
            <div key={s.id} className="flex items-center justify-between text-[12px] border-b border-fab-primary-lighter/60 py-1">
              <span>{s.business_name || s.name}</span>
              <span className="text-fab-text-muted">{s.plan_tier} · {(s.created_at || '').slice(0, 10)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Commissions */}
      <div>
        <div className="text-[13px] font-semibold text-fab-text mb-2">Commissions ({commissions.length})</div>
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full text-[12px]">
            <thead className="text-fab-text-muted text-[10px] uppercase">
              <tr><th className="text-left py-1">Seller / Period</th><th className="text-right">Base</th><th className="text-right">Comm.</th><th className="text-center">Status</th><th className="text-right">Action</th></tr>
            </thead>
            <tbody>
              {commissions.map((c) => (
                <tr key={c.id} className="border-t border-fab-primary-lighter/60" data-testid={`commission-row-${c.id}`}>
                  <td className="py-1.5">{c.seller_name}<div className="text-fab-text-muted">{(c.period_start || '').slice(0, 10)}</div></td>
                  <td className="text-right">{inr(c.base_amount)}</td>
                  <td className="text-right font-semibold">{inr(c.commission_amount)}</td>
                  <td className="text-center"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${COMM_CLS[c.status] || 'bg-neutral-100'}`}>{c.status === 'NEEDS_ADMIN_REVIEW' ? 'REVIEW' : c.status}</span></td>
                  <td className="text-right">
                    {c.status === 'NEEDS_ADMIN_REVIEW' && (
                      <button onClick={() => { setReviewFor(c); setReviewBase(''); }} data-testid={`resolve-review-${c.id}`} className="text-amber-700 underline">Resolve</button>
                    )}
                    {c.status === 'ACCRUED' && !c.refund_adjusted && (
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => refundAdjust(c, 'CANCEL_UNPAID')} data-testid={`refund-cancel-${c.id}`} className="text-rose-600 underline flex items-center gap-0.5"><RotateCcw className="w-3 h-3" />Cancel</button>
                        <button onClick={() => refundAdjust(c, 'DEDUCT_FUTURE')} data-testid={`refund-deduct-${c.id}`} className="text-amber-700 underline">Deduct</button>
                      </div>
                    )}
                    {c.refund_adjusted && <span className="text-fab-text-muted">adjusted</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payouts list */}
      {payouts.length > 0 && (
        <div className="mt-4">
          <div className="text-[13px] font-semibold text-fab-text mb-2">Payouts</div>
          <div className="space-y-1">
            {payouts.map((p) => (
              <div key={p.id} className="flex justify-between text-[12px] border-b border-fab-primary-lighter/60 py-1">
                <span>{p.period_label || '—'} {p.reference ? `· ${p.reference}` : ''}</span>
                <span className="font-semibold">{inr(p.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resolve review inline modal */}
      {reviewFor && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="resolve-review-panel">
          <div className="text-[13px] font-semibold text-amber-800 mb-1">Resolve commission for {reviewFor.seller_name}</div>
          <p className="text-[12px] text-amber-700 mb-2">Enter the verified ex-GST base amount. Commission = base × {Math.round((reviewFor.rate || 0) * 100)}%. This updates the existing record (no duplicate).</p>
          <div className="flex items-center gap-2">
            <input value={reviewBase} onChange={(e) => setReviewBase(e.target.value)} placeholder="Verified ex-GST base ₹" type="number" data-testid="review-base-input" className="fab-input px-3 py-2 rounded-lg border border-amber-300 text-[14px] flex-1" />
            <button onClick={resolveReview} data-testid="review-confirm-btn" className="btn-primary px-4 py-2 text-[13px]">Confirm</button>
            <button onClick={() => setReviewFor(null)} className="px-3 py-2 text-[13px] text-fab-text-muted">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
